import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import site from '../../data/site';
import {sendMessage} from '../../services/messages';
import {SELECT_SERVICE_EVENT} from './ServiceModal';

/*
 * Contact section with a three-way form: Request care / Refer a client / Existing client.
 * Each tab has its own anchor so nav links can open the section on a specific tab:
 *   #contact or #request-care -> Request care, #refer-a-client -> Refer a client, #existing-client -> Existing client.
 */
const TABS = [
    {key: 'request', hash: 'request-care', label: 'Request care'},
    {key: 'refer', hash: 'refer-a-client', label: 'Refer a client'},
    {key: 'existing', hash: 'existing-client', label: 'Existing client'},
];

const tabFromHash = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'contact') {
        return 'request';
    }
    const match = TABS.find((tab) => tab.hash === hash);
    return match ? match.key : null;
};

const CONTACT_METHODS = ['Phone call', 'Email'];
const BEST_TIMES = ['No preference', 'Morning', 'Afternoon'];
const EXISTING_TOPICS = ['Appointment question', 'Care-team question', 'Other'];

const COPY = {
    request: {
        type: 'request_care',
        title: 'Request a callback',
        text: 'Share a few details and the intake team will reach out. Appointments are confirmed by intake.',
        submit: 'Send request',
    },
    refer: {
        type: 'referral',
        title: 'Start a referral',
        text: 'Explore a professional referral request. Do not enter patient information or upload records here.',
        submit: 'Send referral request',
    },
    existing: {
        type: 'existing_client',
        title: 'Contact the office',
        text: 'For questions about appointments or your current care.',
        submit: 'Send message',
    },
};

const Field = ({id, label, children}) => (
    <div className="sw-contact__field">
        <label className="sw-contact__label" htmlFor={id}>{label}</label>
        {children}
    </div>
);

const ContactForm = ({mode, preselect, onPreselectUsed}) => {
    const copy = COPY[mode];
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        full_name: '',
        organization: '',
        contact_method: CONTACT_METHODS[0],
        contact: '',
        service: preselect || site.services[0],
        topic: '',
        best_time: BEST_TIMES[0],
        sms_consent: false,
    });
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error

    // Preselect the service when the user arrives from the "Ask about this service" modal button.
    useEffect(() => {
        if (preselect) {
            setForm((current) => ({...current, service: preselect}));
            onPreselectUsed();
        }
    }, [preselect, onPreselectUsed]);

    const onChange = (e) => {
        const {name, type, checked, value} = e.target;
        setForm((current) => ({...current, [name]: type === 'checkbox' ? checked : value}));
    };

    const byPhone = form.contact_method === 'Phone call';

    const onSubmit = (e) => {
        e.preventDefault();
        setStatus('sending');
        const payload = {
            type: copy.type,
            contact_method: form.contact_method,
            [byPhone ? 'phone_no' : 'email_address']: form.contact,
            best_time: form.best_time,
            sms_consent: byPhone ? form.sms_consent : false,
        };
        if (mode === 'refer') {
            payload.full_name = form.full_name;
            payload.organization = form.organization;
        } else {
            payload.first_name = form.first_name;
            payload.last_name = form.last_name;
        }
        if (mode === 'existing') {
            payload.topic = form.topic;
        } else {
            payload.service = form.service;
        }
        sendMessage(
            payload,
            () => setStatus('sent'),
            (message) => {
                setStatus('error');
                console.log(message);
            }
        );
    };

    if (status === 'sent') {
        return (
            <div className="sw-contact__sent">
                <span className="sw-contact__sent-mark">✓</span>
                <h4>Request received</h4>
                <p>
                    The intake team will follow up using the contact method you chose. No appointment has been
                    booked yet; intake confirms appointments separately.
                </p>
                <button type="button" className="sw-contact__again" onClick={() => {
                    setStatus('idle');
                    setForm((current) => ({...current, contact: '', sms_consent: false}));
                }}>
                    Send another request
                </button>
            </div>
        );
    }

    const prefix = `sw-${mode}`;

    return (
        <form className="sw-contact__form" onSubmit={onSubmit}>
            <h3 className="sw-contact__panel-title">{copy.title}</h3>
            <p className="sw-contact__panel-text">{copy.text}</p>
            <div className="sw-contact__form-row">
                {mode === 'refer' ? (
                    <React.Fragment>
                        <Field id={`${prefix}-name`} label="Your name">
                            <input id={`${prefix}-name`} className="sw-contact__input" type="text" name="full_name"
                                   placeholder="Your name" value={form.full_name} onChange={onChange} required/>
                        </Field>
                        <Field id={`${prefix}-org`} label="Organization">
                            <input id={`${prefix}-org`} className="sw-contact__input" type="text" name="organization"
                                   placeholder="Organization" value={form.organization} onChange={onChange} required/>
                        </Field>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Field id={`${prefix}-first`} label="First name">
                            <input id={`${prefix}-first`} className="sw-contact__input" type="text" name="first_name"
                                   placeholder="First name" value={form.first_name} onChange={onChange} required/>
                        </Field>
                        <Field id={`${prefix}-last`} label="Last name">
                            <input id={`${prefix}-last`} className="sw-contact__input" type="text" name="last_name"
                                   placeholder="Last name" value={form.last_name} onChange={onChange} required/>
                        </Field>
                    </React.Fragment>
                )}
            </div>
            <Field id={`${prefix}-method`} label="Preferred contact method">
                <select id={`${prefix}-method`} className="sw-contact__select" name="contact_method"
                        value={form.contact_method} onChange={onChange}>
                    {CONTACT_METHODS.map((method) => <option key={method} value={method}>{method}</option>)}
                </select>
            </Field>
            <Field id={`${prefix}-contact`} label={byPhone ? 'Phone number' : 'Email address'}>
                <input id={`${prefix}-contact`} className="sw-contact__input" type={byPhone ? 'tel' : 'email'}
                       name="contact" placeholder={byPhone ? 'Phone number' : 'you@example.com'}
                       value={form.contact} onChange={onChange} required/>
            </Field>
            {mode === 'existing' ? (
                <Field id={`${prefix}-topic`} label="What do you need help with?">
                    <select id={`${prefix}-topic`} className="sw-contact__select" name="topic"
                            value={form.topic} onChange={onChange} required>
                        <option value="" disabled>Select a topic</option>
                        {EXISTING_TOPICS.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                    </select>
                </Field>
            ) : (
                <Field id={`${prefix}-service`} label="Service of interest">
                    <select id={`${prefix}-service`} className="sw-contact__select" name="service"
                            value={form.service} onChange={onChange}>
                        {site.services.map((service) => <option key={service} value={service}>{service}</option>)}
                    </select>
                </Field>
            )}
            <Field id={`${prefix}-time`} label="Best time to reach you">
                <select id={`${prefix}-time`} className="sw-contact__select" name="best_time"
                        value={form.best_time} onChange={onChange}>
                    {BEST_TIMES.map((time) => <option key={time} value={time}>{time}</option>)}
                </select>
            </Field>
            <p className="sw-contact__note">
                Please do not enter medical history, diagnoses, or patient records in this form.
            </p>
            {byPhone && (
                <label className="sw-contact__consent">
                    <input type="checkbox" name="sms_consent" checked={form.sms_consent} onChange={onChange}/>
                    <span>
                        By checking this box, I agree to receive Customer Care SMS from {site.name}.
                        Reply STOP to opt-out; Reply HELP for support; Message &amp; data rates may apply;
                        Messaging frequency may vary. Visit{' '}
                        <Link to="/privacy-policy">https://swahealthmd.com/privacy-policy</Link> to view our
                        privacy policy and{' '}
                        <Link to="/terms-and-conditions">https://swahealthmd.com/terms-and-conditions</Link> to
                        view our terms.
                    </span>
                </label>
            )}
            {status === 'error' && (
                <p className="sw-contact__error">
                    Something went wrong sending your request. Please try again or call us directly.
                </p>
            )}
            <button className="sw-contact__submit" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : copy.submit}
            </button>
            <p className="sw-contact__legal">
                By submitting, you agree to the{' '}
                <Link to="/terms-and-conditions">Terms and Conditions</Link> and{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
        </form>
    );
};

const ContactSection = () => {
    const [tab, setTab] = useState(() => tabFromHash() || 'request');
    const [preselect, setPreselect] = useState('');

    useEffect(() => {
        const onHashChange = () => {
            const next = tabFromHash();
            if (next) {
                setTab(next);
            }
        };
        const onSelectService = (e) => {
            setPreselect(e.detail);
            setTab('request');
        };
        window.addEventListener('hashchange', onHashChange);
        window.addEventListener(SELECT_SERVICE_EVENT, onSelectService);
        return () => {
            window.removeEventListener('hashchange', onHashChange);
            window.removeEventListener(SELECT_SERVICE_EVENT, onSelectService);
        };
    }, []);

    const selectTab = (next) => {
        setTab(next.key);
        // Keep the URL in sync without triggering a scroll jump.
        window.history.replaceState(null, '', `#${next.hash}`);
    };

    const primaryPhone = site.phones[0];

    return (
        <div id="contact" className="sw-contact">
            {TABS.map((item) => (
                <span key={item.hash} id={item.hash} className="sw-contact__anchor" aria-hidden="true"/>
            ))}
            <div className="sw-contact__grid-bg"/>
            <div className="sw-container sw-contact__inner">
                <div className="sw-contact__info">
                    <p className="sw-eyebrow sw-eyebrow--cyan">Let’s take the first step</p>
                    <h2 className="sw-heading sw-contact__title">You can start<br/>with a conversation.</h2>
                    <p className="sw-contact__lede">
                        Ask about services, insurance, and getting started. For urgent help, use the crisis
                        resources below rather than this form.
                    </p>
                    <a className="sw-contact__phone" href={primaryPhone.href}>{primaryPhone.label}</a>
                    <a className="sw-contact__email" href={`mailto:${site.email}`}>{site.email}</a>
                    <div className="sw-contact__hours">
                        <h4>Office hours</h4>
                        <p>{site.officeHours}</p>
                        <h4>Walk-in hours</h4>
                        <p>{site.walkInHours}</p>
                    </div>
                    <div className="sw-contact__address">
                        <strong>Visit our Baltimore office</strong>
                        <p>
                            {site.address.lines.map((line) => (
                                <React.Fragment key={line}>{line}<br/></React.Fragment>
                            ))}
                        </p>
                        <a href={site.address.mapsUrl} target="_blank" rel="noopener noreferrer">View directions</a>
                        <p className="sw-contact__address-note">
                            Walk-ins are welcome during the hours listed above. Call intake with questions about
                            services or appointment availability.
                        </p>
                    </div>
                    <div className="sw-contact__crisis">
                        <strong>Need immediate help?</strong>
                        <p>
                            This form is not for emergencies. For immediate danger, call 911. For crisis support,
                            call or text <a href="tel:988">988</a>.
                        </p>
                    </div>
                </div>
                <div className="sw-contact__form-col">
                    <div className="sw-contact__form-card">
                        <div className="sw-contact__tabs" role="tablist" aria-label="Contact purpose">
                            {TABS.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === item.key}
                                    className={`sw-contact__tab${tab === item.key ? ' is-active' : ''}`}
                                    onClick={() => selectTab(item)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <ContactForm
                            key={tab}
                            mode={tab}
                            preselect={tab === 'request' ? preselect : ''}
                            onPreselectUsed={() => setPreselect('')}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactSection;
